import AddressVerifyCard from "@/components/AddressVerifyCard";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill, ArrowLeft, Save, ArrowRight, User, MapPin, Phone, Heart, AlertTriangle, X, Search, Check, LogOut, Mail, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import ActiveSessionsCard from "@/components/ActiveSessionsCard";
import MFACard from "@/components/MFACard";
import PhiAccessCard from "@/components/PhiAccessCard";
import ReceiptsCard from "@/components/ReceiptsCard";
import CompanionAccessRedeemCard from "@/components/CompanionAccessRedeemCard";
import NotificationPrefsCard from "@/components/NotificationPrefsCard";
import DeleteAccountCard from "@/components/DeleteAccountCard";
import SubscriptionManagerCard from "@/components/patient/SubscriptionManagerCard";
import DobInput from "@/components/DobInput";
import YouTubeLibraryCard from "@/components/YouTubeLibraryCard";
import ProfileFitQuizCard from "@/components/ProfileFitQuizCard";
import PublicFooter from "@/components/PublicFooter";
import { useChatHidden, setChatHidden } from "@/hooks/useChatWidgetHidden";
import { MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];

/**
 * "Chat widget" preferences card — surfaces a "Show chat" button only
 * when the launcher is currently hidden (patient tapped the eye-off).
 * The row disappears once chat is visible again to keep the profile
 * clean for the 99% of users who never touch the eye-off button.
 */
function ChatWidgetPreferenceCard() {
  const hidden = useChatHidden();
  if (!hidden) return null;
  return (
    <section className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6" data-testid="chat-restore-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-5 h-5 text-purple-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Chat widget hidden
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            You dismissed the chat launcher earlier this session. Bring it back with a click.
          </p>
        </div>
        <Button
          onClick={() => setChatHidden(false)}
          className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
          data-testid="chat-restore-btn"
        >
          Show chat
        </Button>
      </div>
    </section>
  );
}



function DrugSearch({ medications, setMedications }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await API.get(`/drugs/search?q=${encodeURIComponent(q)}`);
      setResults(data.filter(d => !medications.includes(d)));
      setShowResults(true);
    } catch { setResults([]); } finally { setLoading(false); }
  }, [medications]);

  const handleInput = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 400);
  };

  const addDrug = (drug) => {
    if (!medications.includes(drug)) {
      setMedications([...medications, drug]);
    }
    setQuery("");
    setShowResults(false);
    setResults([]);
  };

  const removeDrug = (drug) => {
    setMedications(medications.filter(m => m !== drug));
  };

  return (
    <div>
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => handleInput(e.target.value)}
            placeholder="Search medications (e.g. Lisinopril, Metformin)..."
            className="rounded-xl pl-10"
            data-testid="drug-search-input"
          />
        </div>
        {loading && <div className="absolute right-3 top-3"><div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
        {showResults && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="drug-suggestions">
            {results.map((drug) => (
              <button key={drug} onClick={() => addDrug(drug)}
                className="w-full text-left px-4 py-2.5 hover:bg-purple-50 border-b border-slate-100 last:border-0 text-sm text-slate-700 transition-colors">
                {drug}
              </button>
            ))}
          </div>
        )}
      </div>
      {medications.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="drug-list">
          {medications.map((drug) => (
            <div key={drug} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 group">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-400" />
                <span className="text-sm text-slate-700">{drug}</span>
              </div>
              <button onClick={() => removeDrug(drug)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-drug-${drug}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PatientProfile() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get("from") === "onboarding";
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nkda, setNkda] = useState(false);
  const [medicationsList, setMedicationsList] = useState([]);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  // Address verification state — must be `verified` before save is allowed.
  const [addrStatus, setAddrStatus] = useState("pristine");
  // Email change modal
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeForm, setEmailChangeForm] = useState({ new_email: "", password: "" });
  const [emailChangeSubmitting, setEmailChangeSubmitting] = useState(false);

  // Address verification persistence — fingerprint of "street|line2|city|state|zip"
  // is stored on the user record at Verify time. If today's fingerprint matches
  // the stored one we treat the address as already verified across sessions; if
  // any field changes the fingerprint mismatches and the user has to re-verify.
  // Normalization is intentionally aggressive: collapse all whitespace runs to a
  // single space, drop punctuation that doesn't matter for delivery (.,'), and
  // strip non-breaking spaces — small invisible drift between Mapbox-normalized
  // saves and form-rendered loads was forcing patients to re-verify each login.
  const buildAddrFingerprint = (p) => {
    if (!p) return "";
    const norm = (v, upper = false) => {
      const cleaned = (v || "")
        .replace(/\u00a0/g, " ")          // non-breaking → regular space
        .replace(/[.,'`]/g, "")           // strip punctuation that varies in USPS data
        .replace(/\s+/g, " ")             // collapse whitespace runs
        .trim();
      return upper ? cleaned.toUpperCase() : cleaned.toLowerCase();
    };
    return [
      norm(p.address_street),
      norm(p.address_line2),
      norm(p.address_city),
      norm(p.address_state, true),
      norm(p.address_zip),
    ].join("|");
  };
  const currentFingerprint = buildAddrFingerprint(profile);
  const isAddressLocked = !!(
    profile?.address_verified_fingerprint &&
    profile.address_verified_fingerprint === currentFingerprint &&
    profile?.address_disclaimer_accepted
  );

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await API.get("/profile");
      setProfile(data);
      setNkda(data.allergies_nkda || data.allergies === "NKDA");
      setMedicationsList(data.current_medications_list || []);
      setDisclaimerChecked(data.address_disclaimer_accepted || false);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleChange = (key, value) => {
    setProfile({ ...profile, [key]: value });
  };

  const handleAddressSelect = (s) => {
    setProfile({
      ...profile,
      address_street: s.street,
      address_city: s.city,
      address_state: s.state,
      address_zip: s.zip,
    });
  };

  const validateRequired = () => {
    const required = ["name", "phone", "date_of_birth", "gender", "address_street", "address_city", "address_state", "address_zip"];
    const missing = required.filter(f => !profile?.[f]?.trim());
    if (missing.length > 0) {
      toast.error(`Please fill in all required fields: ${missing.map(f => f.replace(/_/g, ' ')).join(', ')}`);
      return false;
    }
    if (!disclaimerChecked) {
      toast.error("Please accept the address disclaimer before saving.");
      return false;
    }
    return true;
  };

  const submitEmailChange = async () => {
    const newEmail = (emailChangeForm.new_email || "").trim().toLowerCase();
    if (!newEmail || !emailChangeForm.password) {
      toast.error("Enter your new email and current password");
      return;
    }
    setEmailChangeSubmitting(true);
    try {
      const { data } = await API.post("/profile/email-change/request", {
        new_email: newEmail,
        password: emailChangeForm.password,
      });
      toast.success(data.message || "Verification email sent.");
      setShowEmailChange(false);
      setEmailChangeForm({ new_email: "", password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start email change");
    } finally {
      setEmailChangeSubmitting(false);
    }
  };

  const handleSave = async (goNext = false) => {
    if (!validateRequired()) return;
    // HARD GATE: address must be verified by Mapbox before save is allowed —
    // unless we already have a stored verified fingerprint that matches the
    // current address (then it's locked and the user doesn't need to re-verify).
    if (addrStatus !== "verified" && !isAddressLocked) {
      toast.error("Please click \"Verify Address\" first — we can't ship to addresses that aren't verified.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...profile,
        allergies_nkda: nkda,
        allergies: nkda ? "NKDA" : (profile.allergies || ""),
        current_medications_list: medicationsList,
        current_medications: medicationsList.length > 0 ? medicationsList.join(", ") : (profile.current_medications || ""),
        medical_history: profile.medical_history?.trim() ? profile.medical_history : "N/A",
        address_disclaimer_accepted: disclaimerChecked,
      };
      const { data } = await API.put("/profile", payload);
      // Persist the verified fingerprint server-side so the green badge sticks
      // across logouts. Only fire if we just verified (not on a locked-no-op save).
      // CRITICAL: we MUST merge the fresh fingerprint into the local profile
      // state we set below — otherwise `setProfile(data)` would overwrite the
      // freshly-acknowledged fingerprint with the empty/old value from the PUT
      // response, and the patient would re-see the unverified UI until a full
      // page reload (the symptom reported as "have to re-verify every login").
      let ackFingerprint = data.address_verified_fingerprint || null;
      if (addrStatus === "verified" && disclaimerChecked) {
        try {
          await API.post("/address/acknowledge", { fingerprint: currentFingerprint });
          ackFingerprint = currentFingerprint;
        } catch (ackErr) {
          // Don't fail the save, but DO surface this — silently swallowing
          // was hiding a persistence bug from patients.
          console.warn("address acknowledge failed:", ackErr);
          toast.error("Profile saved, but the address-verified badge couldn't persist. Please try saving again.");
        }
      }
      // Merge the canonical fingerprint into local state so the green
      // "verified across sessions" lock activates immediately without needing
      // a refresh.
      setProfile({
        ...data,
        address_verified_fingerprint: ackFingerprint,
        address_disclaimer_accepted: disclaimerChecked,
      });
      await checkAuth();
      toast.success("Profile saved successfully");
      if (goNext) {
        navigate("/questionnaire?from=onboarding");
      }
    } catch (e) {
      toast.error("Failed to save profile");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading profile...</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-profile-page">
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Button variant="ghost" className="rounded-full text-slate-600" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {isOnboarding ? "Complete Your Profile" : "Your Profile"}
        </h1>
        <p className="text-slate-600 mb-8">
          {isOnboarding ? "Please fill in all required fields to continue with your onboarding." : "Keep your demographics and medical information up to date."}
        </p>

        <div className="space-y-8">
          {/* Personal Info */}
          <section className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"><User className="w-5 h-5 text-purple-600" /></div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Personal Information</h2>
                <p className="text-xs text-slate-500">All fields are required</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Full Name <span className="text-red-500">*</span></Label>
                <Input value={profile?.name || ""} onChange={(e) => handleChange("name", e.target.value)} className="mt-1 rounded-xl" data-testid="profile-name" required />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={profile?.email || ""} disabled className="rounded-xl bg-slate-50 flex-1" data-testid="profile-email" />
                  <Button type="button" variant="outline" className="rounded-xl border-purple-200 text-purple-700 hover:bg-purple-50 shrink-0" onClick={() => setShowEmailChange(true)} data-testid="profile-email-change-btn">
                    <Mail className="w-3.5 h-3.5 mr-1" /> Change
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">We&apos;ll send a verification link to your new address before the change takes effect.</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Phone <span className="text-red-500">*</span></Label>
                <Input value={profile?.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} placeholder="(555) 123-4567" className="mt-1 rounded-xl" data-testid="profile-phone" required />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Date of Birth <span className="text-red-500">*</span></Label>
                <DobInput value={profile?.date_of_birth || ""} onChange={(iso) => handleChange("date_of_birth", iso)} className="mt-1" data-testid="profile-dob" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Gender <span className="text-red-500">*</span></Label>
                <Select value={profile?.gender || ""} onValueChange={(v) => handleChange("gender", v)}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="profile-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-Binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Address */}
          <section className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><MapPin className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Shipping Address</h2>
                <p className="text-xs text-slate-500">All address fields are required (except Gate/Access Code)</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">Street Address <span className="text-red-500">*</span></Label>
                <AddressAutocomplete
                  value={profile?.address_street || ""}
                  onChange={(val) => handleChange("address_street", val)}
                  onSelect={handleAddressSelect}
                  placeholder="Start typing your address..."
                  testId="profile-street"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">Apt / Suite / Unit / Lot <span className="text-slate-400 font-normal">(Optional)</span></Label>
                <Input value={profile?.address_line2 || ""} onChange={(e) => handleChange("address_line2", e.target.value)} placeholder="Apt 4B, Suite 200, Lot 17, Building C…" className="mt-1 rounded-xl" data-testid="profile-address-line2" />
                <p className="text-[11px] text-slate-400 mt-1">Include this if your address has a unit number — helps couriers deliver to the exact door.</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">City <span className="text-red-500">*</span></Label>
                <Input value={profile?.address_city || ""} onChange={(e) => handleChange("address_city", e.target.value)} placeholder="Tampa" className="mt-1 rounded-xl" data-testid="profile-city" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">State <span className="text-red-500">*</span></Label>
                  <Select value={profile?.address_state || ""} onValueChange={(v) => handleChange("address_state", v)}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="profile-state"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">ZIP <span className="text-red-500">*</span></Label>
                  <Input value={profile?.address_zip || ""} onChange={(e) => handleChange("address_zip", e.target.value)} placeholder="33601" className="mt-1 rounded-xl" data-testid="profile-zip" required />
                </div>
              </div>

              {/* Address verification gate — uses shared AddressVerifyCard. */}
              <div className="md:col-span-2">
                <AddressVerifyCard
                  street={profile?.address_street}
                  city={profile?.address_city}
                  state={profile?.address_state}
                  zip={profile?.address_zip}
                  resetSignal={`${profile?.address_street}|${profile?.address_line2}|${profile?.address_city}|${profile?.address_state}|${profile?.address_zip}`}
                  onStatusChange={setAddrStatus}
                  locked={isAddressLocked}
                  onVerified={(n) => setProfile(p => ({
                    ...p,
                    address_city: n.city || p.address_city,
                    address_state: n.state || p.address_state,
                    address_zip: n.postal_code || p.address_zip,
                  }))}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">Gate / Access Code <span className="text-slate-400 font-normal">(Optional)</span></Label>
                <Input value={profile?.address_gate_code || ""} onChange={(e) => handleChange("address_gate_code", e.target.value)} placeholder="Gate code, buzzer #, building access code, etc." className="mt-1 rounded-xl" data-testid="profile-gate-code" />
              </div>
            </div>

            {/* Address Disclaimer — turns green once acknowledged. Locked
                (read-only "Verified" state) when the patient has already
                acknowledged this exact address; auto-unlocks if any field
                changes. */}
            <div className={`mt-6 border rounded-xl p-4 transition-colors ${disclaimerChecked ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-200"}`} data-testid="address-disclaimer">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="address-disclaimer"
                  checked={disclaimerChecked}
                  onCheckedChange={(v) => setDisclaimerChecked(!!v)}
                  disabled={isAddressLocked}
                  className={`mt-0.5 ${disclaimerChecked ? "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" : "data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"}`}
                  data-testid="address-disclaimer-checkbox"
                />
                <label htmlFor="address-disclaimer" className={`text-sm leading-relaxed cursor-pointer flex-1 ${disclaimerChecked ? "text-emerald-900" : "text-amber-900"}`}>
                  <span className="font-semibold flex items-center gap-1 mb-1">
                    {isAddressLocked ? (
                      <><Check className="w-4 h-4 text-emerald-600" /> Address verified &amp; acknowledged</>
                    ) : disclaimerChecked ? (
                      <><Check className="w-4 h-4 text-emerald-600" /> Acknowledged — please verify your address above</>
                    ) : (
                      <><AlertTriangle className="w-4 h-4 text-amber-600" /> Please verify your address carefully</>
                    )}
                  </span>
                  Triple check your address is correct as this is the address that will be on your prescription and the address that the pharmacy will ship to (unless specifically notated). If your address is entered incorrectly by you (the patient/customer) you will be at fault and will have to pay for another shipment. Make sure to add any suite #, apartment #, unit #, lot number, building number OR any gate/access code in the field above.
                  {isAddressLocked && (
                    <span className="block mt-2 text-xs text-emerald-700">Edit any address field above to re-verify.</span>
                  )}
                </label>
              </div>
            </div>
          </section>

          {/* Emergency Contact - OPTIONAL */}
          <section className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><Phone className="w-5 h-5 text-red-600" /></div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Emergency Contact</h2>
              </div>
            </div>
            <p className="text-xs text-slate-400 italic mb-6 ml-[52px]">This section is optional. Information provided here will only be used for medical emergencies and will not be used for any soliciting purposes.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Contact Name</Label>
                <Input value={profile?.emergency_contact_name || ""} onChange={(e) => handleChange("emergency_contact_name", e.target.value)} placeholder="Jane Doe" className="mt-1 rounded-xl" data-testid="profile-emergency-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Contact Phone</Label>
                <Input value={profile?.emergency_contact_phone || ""} onChange={(e) => handleChange("emergency_contact_phone", e.target.value)} placeholder="(555) 987-6543" className="mt-1 rounded-xl" data-testid="profile-emergency-phone" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Relationship</Label>
                <Input value={profile?.emergency_contact_relationship || ""} onChange={(e) => handleChange("emergency_contact_relationship", e.target.value)} placeholder="Spouse" className="mt-1 rounded-xl" data-testid="profile-emergency-rel" />
              </div>
            </div>
          </section>

          {/* Medical Info */}
          <section className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Heart className="w-5 h-5 text-emerald-600" /></div>
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Medical Information</h2>
            </div>
            <div className="space-y-6">
              {/* Medical History */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Medical History</Label>
                <p className="text-xs text-slate-400 mb-1">List relevant conditions, surgeries, or hospitalizations. Leave blank to auto-fill as N/A.</p>
                <Textarea value={profile?.medical_history || ""} onChange={(e) => handleChange("medical_history", e.target.value)} placeholder="List any relevant medical conditions... (auto-fills N/A if left blank)" className="mt-1 rounded-xl min-h-[100px]" data-testid="profile-medical-history" />
              </div>

              {/* Allergies with NKDA */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Known Allergies</Label>
                <div className="flex items-center gap-3 mt-2 mb-2">
                  <Checkbox
                    id="nkda-check"
                    checked={nkda}
                    onCheckedChange={(checked) => {
                      setNkda(checked);
                      if (checked) handleChange("allergies", "NKDA");
                    }}
                    className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    data-testid="nkda-checkbox"
                  />
                  <label htmlFor="nkda-check" className="text-sm text-slate-600 cursor-pointer flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600" /> NKDA — No Known Drug Allergies
                  </label>
                </div>
                {!nkda && (
                  <Textarea value={profile?.allergies || ""} onChange={(e) => handleChange("allergies", e.target.value)} placeholder="List any drug or food allergies..." className="rounded-xl min-h-[80px]" data-testid="profile-allergies" />
                )}
                {nkda && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 font-medium">NKDA — No Known Drug Allergies</div>
                )}
              </div>

              {/* Current Medications - Drug Search */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Current Medications</Label>
                <p className="text-xs text-slate-400 mb-2">Search and select from the national drug database (RxNorm). You can add or remove medications.</p>
                <DrugSearch medications={medicationsList} setMedications={setMedicationsList} />
              </div>

              {/* Conditions / Diagnosis */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Conditions / Diagnosis</Label>
                <p className="text-xs text-slate-400 mb-1">List any conditions you have (e.g., high blood pressure, insomnia, diabetes, neuropathy).</p>
                <Textarea value={profile?.conditions_diagnosis || ""} onChange={(e) => handleChange("conditions_diagnosis", e.target.value)} placeholder="List any conditions or diagnoses..." className="mt-1 rounded-xl min-h-[80px]" data-testid="profile-conditions-diagnosis" />
              </div>
            </div>
          </section>

          {/* Save Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            {isOnboarding ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSave(false).then(() => navigate("/dashboard"))}
                  disabled={saving}
                  className="rounded-full border-slate-300 text-slate-600 px-8 py-6 text-base"
                  data-testid="save-continue-later-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Save & Continue Later
                </Button>
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-base"
                  data-testid="save-next-step-btn"
                >
                  {saving ? "Saving..." : "Save & Go to Next Step"} {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </>
            ) : (
              <Button onClick={() => handleSave(false)} disabled={saving} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-base" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Profile"}
              </Button>
            )}
          </div>
        </div>

        {/* Security — Two-Factor Auth + Active Sessions (HIPAA) */}
        <div className="mt-6 space-y-4">
          <ProfileFitQuizCard />
          <CompanionAccessRedeemCard />
          <NotificationPrefsCard />
          <ChatWidgetPreferenceCard />
          <SubscriptionManagerCard />
          <ReceiptsCard />
          <YouTubeLibraryCard />
          <MFACard audience="patient" />
          <ActiveSessionsCard />
          <PhiAccessCard />
          <DeleteAccountCard />
        </div>

        {/* Help — quick access to FAQ + contact, so patients never have
            to leave their profile to look something up. */}
        <section className="mt-6 bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8" data-testid="profile-help-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Help &amp; FAQ</h2>
              <p className="text-xs text-slate-500">Common questions about your medication, billing, shipping &amp; more.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link to="/faq" className="rounded-xl border border-purple-200 bg-purple-50 hover:bg-purple-100 px-4 py-3 text-sm font-semibold text-purple-700 text-center transition-colors" data-testid="profile-faq-link">
              Browse FAQ
            </Link>
            <a href="tel:+17275162142" className="rounded-xl border border-slate-200 hover:border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 text-center transition-colors" data-testid="profile-call-primary">
              Call (727) 516-2142
            </a>
            <a href="tel:+18887743320" className="rounded-xl border border-slate-200 hover:border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 text-center transition-colors" data-testid="profile-call-secondary">
              Or (888) 774-3320
            </a>
          </div>
        </section>
      </div>

      {/* Email change dialog */}
      <Dialog open={showEmailChange} onOpenChange={(open) => { if (!open) { setShowEmailChange(false); setEmailChangeForm({ new_email: "", password: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="email-change-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Change your sign-in email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">We&apos;ll send a verification link to your <b>new</b> email. The change takes effect only after you click that link (within 2 hours).</p>
            <div>
              <Label className="text-xs font-medium text-slate-600">New email</Label>
              <Input type="email" autoFocus value={emailChangeForm.new_email} onChange={(e) => setEmailChangeForm({ ...emailChangeForm, new_email: e.target.value })} placeholder="new-address@example.com" className="mt-1 rounded-xl" data-testid="email-change-new-email" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Current password</Label>
              <Input type="password" value={emailChangeForm.password} onChange={(e) => setEmailChangeForm({ ...emailChangeForm, password: e.target.value })} placeholder="To confirm it's really you" className="mt-1 rounded-xl" data-testid="email-change-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEmailChange(false); setEmailChangeForm({ new_email: "", password: "" }); }}>Cancel</Button>
            <Button onClick={submitEmailChange} disabled={emailChangeSubmitting || !emailChangeForm.new_email || !emailChangeForm.password} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="email-change-submit">
              {emailChangeSubmitting ? "Sending…" : "Send verification link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublicFooter />
    </div>
  );
}
